// ─── FilterPanel.jsx ─────────────────────────────────────────────────────────
// Drop-in filter grid shared by WorkerOnboardingPage and SuperAdminDashboard.
//
// Props
// ──────
//  filters        – the full object returned by useWorkerFilters()
//  onReset        – called whenever any filter changes (page resets results)
//  showOnboardingFilters – boolean (default true)
//    When true  → renders the full set used on the Onboarding page
//              (Company, CompanyCheck, LastWorkedOn, LastCallStatus, Aadhaar,
//               KYC, RefCheck, Blacklisted, LastWorkedOnGlobal)
//    When false → renders only the filters used on the SuperAdmin Dashboard
//              (Location, Skills, WorkerType, Gender, Source, RegisteredOn)
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import {
  LocationFilter,
  SkillsFilter,
  WorkerTypeFilter,
  GenderFilter,
  SourceFilter,
  AadhaarFilter,
  QualificationFilter,
  KycFilter,
  RefCheckFilter,
  CallCategoryFilter,
  CompanyCheckFilter,
  CompanyLastWorkedOnFilter,
  BlacklistedFilter,
  LastCompanyFilter,
  AllTimeCompanyFilter,
  RegisteredOnFilter,
  LastWorkedOnFilter,
  LastCallStatusFilter,
  LastCallSubStatusFilter,

  PaymentverificationFilter,
  HasAppTokenFilter,
  ShiftCountFilter,
  PoliceVerificationFilter,
  MarksheetFilter,  // ← Keep as MarksheetFilter
  EShramFilter,
} from "./FilterSection";

export function FilterPanel({ filters, onReset, showOnboardingFilters = true }) {
  const {
    // option arrays
    locationOptions, skillOptions, companyOptions,sourceOptions, lastCallStatusOptions,lastCallSubStatusOptions,

    // loading flags
    fetchingLocations, fetchingSkills, fetchingCompanies,fetchingSources,
    fetchingStatuses,fetchingSubStatuses,  statusFilterError,subStatusFilterError,

    // values + setters
    selectedLocations,        setSelectedLocations,
    selectedSkills,           setSelectedSkills,
    selectedWorkerTypes,      setSelectedWorkerTypes,
    selectedGenders,          setSelectedGenders,
    selectedSources,          setSelectedSources,
    selectedAadhaar,          setSelectedAadhaar,
    Highestqualification,     setHighestqualification,
    selectedKyc,              setSelectedKyc,
    selectedRefStatus,        setSelectedRefStatus,
    selectedCompanyCheck,     setSelectedCompanyCheck,
    selectedBlacklisted,      setSelectedBlacklisted,
    selectedpaymentverification,  setselectedpaymentverification,
    selectedLastCompanies,    setSelectedLastCompanies,
    selectedAllTimeCompanies, setSelectedAllTimeCompanies,
    selectedLastCallStatus,   setSelectedLastCallStatus,
    selectedLastCallSubStatus,   setSelectedLastCallSubStatus,
    selectedCallCategory,     setSelectedCallCategory,
    registeredOnRange,        setRegisteredOnRange,
    lastWorkedOnRange,        setLastWorkedOnRange,
    companyLastWorkedOnRange, setCompanyLastWorkedOnRange,
    activeCompanyFilter,      setActiveCompanyFilter,

    // NEW filter values
    selectedPoliceVerification, setSelectedPoliceVerification,
    selectedMarksheet, setSelectedMarksheet,  // ← Keep as marksheet
    selectedEshram,             setSelectedEshram,

    // derived
    companyFiltersEnabled,
    activeCompanySelections,
    companyCheckEnabled,
    selectedCompanyName,
    selectedHasAppToken,
    setSelectedHasAppToken,
    shiftCountRange,
    setShiftCountRange,
  } = filters;

  // Helper function to handle filter changes with reset
  const handleFilterChange = (setter, value) => {
    if (typeof setter === 'function') {
      setter(value);
    }
    if (typeof onReset === 'function') {
      onReset();
    }
  };

  // For special cases where we need more control
  const handleComplexFilterChange = (setter, additionalLogic) => {
    return (value) => {
      if (typeof setter === 'function') {
        setter(value);
      }
      if (typeof additionalLogic === 'function') {
        additionalLogic(value);
      }
      if (typeof onReset === 'function') {
        onReset();
      }
    };
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">

      {/* ── Always visible ── */}
      <LocationFilter
        selected={selectedLocations}
        onSelect={(value) => handleFilterChange(setSelectedLocations, value)}
        resetResults={onReset}
        options={locationOptions}
        loading={fetchingLocations}
      />

      <SkillsFilter
        selected={selectedSkills}
        onSelect={(value) => handleFilterChange(setSelectedSkills, value)}
        resetResults={onReset}
        options={skillOptions}
        loading={fetchingSkills}
      />

      <WorkerTypeFilter
        selected={selectedWorkerTypes}
        onSelect={(value) => handleFilterChange(setSelectedWorkerTypes, value)}
        resetResults={onReset}
      />

      <GenderFilter
        selected={selectedGenders}
        onSelect={(value) => handleFilterChange(setSelectedGenders, value)}
        resetResults={onReset}
      />

      <SourceFilter
        selected={selectedSources}
        onSelect={(value) => handleFilterChange(setSelectedSources, value)}
        resetResults={onReset}
        options={filters.sourceOptions || ["(Blanks)", "App", "Field Agent", "WhatsApp"]}
        loading={filters.fetchingSources || false}
      />

      <RegisteredOnFilter
        startDate={registeredOnRange.startDate}
        endDate={registeredOnRange.endDate}
        onChange={(value) => handleFilterChange(setRegisteredOnRange, value)}
        resetResults={onReset}
      />

      {/* ── Onboarding-only filters ── */}
      {showOnboardingFilters && (
        <>
          <LastCompanyFilter
            selected={selectedLastCompanies}
            onSelect={(v) => {
              if (typeof setSelectedLastCompanies === 'function') {
                setSelectedLastCompanies(v);
              }
              if (typeof setSelectedAllTimeCompanies === 'function') {
                setSelectedAllTimeCompanies([]);
              }
              if (typeof setActiveCompanyFilter === 'function') {
                setActiveCompanyFilter(v.length > 0 ? "last" : null);
              }
              if (typeof onReset === 'function') {
                onReset();
              }
            }}
            resetResults={onReset}
            options={companyOptions}
            loading={fetchingCompanies}
            disabled={!companyFiltersEnabled || activeCompanyFilter === "allTime"}
            disabledReason={
              !companyFiltersEnabled
                ? "Select a location first to load companies"
                : activeCompanyFilter === "allTime"
                ? "Clear the All-time Company filter first"
                : undefined
            }
            setActiveCompanyFilter={setActiveCompanyFilter}
            activeCompanyFilter={activeCompanyFilter}
          />

          <AllTimeCompanyFilter
            selected={selectedAllTimeCompanies}
            onSelect={(v) => {
              if (typeof setSelectedAllTimeCompanies === 'function') {
                setSelectedAllTimeCompanies(v);
              }
              if (typeof setSelectedLastCompanies === 'function') {
                setSelectedLastCompanies([]);
              }
              if (typeof setActiveCompanyFilter === 'function') {
                setActiveCompanyFilter(v.length > 0 ? "allTime" : null);
              }
              if (typeof onReset === 'function') {
                onReset();
              }
            }}
            resetResults={onReset}
            options={companyOptions}
            loading={fetchingCompanies}
            disabled={!companyFiltersEnabled || activeCompanyFilter === "last"}
            disabledReason={
              !companyFiltersEnabled
                ? "Select a location first to load companies"
                : activeCompanyFilter === "last"
                ? "Clear the Last Company filter first"
                : undefined
            }
          />

          <AadhaarFilter
            selected={selectedAadhaar}
            onSelect={(value) => handleFilterChange(setSelectedAadhaar, value)}
            resetResults={onReset}
          />

          <QualificationFilter
            selected={Highestqualification}
            onSelect={(value) => handleFilterChange(setHighestqualification, value)}
            resetResults={onReset}
          />

          <KycFilter
            selected={selectedKyc}
            onSelect={(value) => handleFilterChange(setSelectedKyc, value)}
            resetResults={onReset}
          />


          <RefCheckFilter
            selected={selectedRefStatus}
            onSelect={(value) => handleFilterChange(setSelectedRefStatus, value)}
            resetResults={onReset}
          />

          <CompanyCheckFilter
            selected={selectedCompanyCheck}
            onSelect={(value) => handleFilterChange(setSelectedCompanyCheck, value)}
            resetResults={onReset}
            disabled={!companyCheckEnabled}
            disabledReason={
              activeCompanySelections.length === 0
                ? "Select exactly one company (Last or All-time) to enable this filter"
                : activeCompanySelections.length > 1
                ? "Narrow down to one company — Company Check can't span multiple"
                : undefined
            }
          />

          <CompanyLastWorkedOnFilter
            startDate={companyLastWorkedOnRange.startDate}
            endDate={companyLastWorkedOnRange.endDate}
            onChange={(value) => handleFilterChange(setCompanyLastWorkedOnRange, value)}
            resetResults={onReset}
            disabled={!companyCheckEnabled}
            disabledReason={
              activeCompanySelections.length === 0
                ? "Select exactly one company (Last or All-time) to enable this filter"
                : activeCompanySelections.length > 1
                ? "Narrow down to one company to enable this filter"
                : undefined
            }
            companyName={selectedCompanyName}
          />

          <BlacklistedFilter
            selected={selectedBlacklisted}
            onSelect={(value) => handleFilterChange(setSelectedBlacklisted, value)}
            resetResults={onReset}
          />

          <LastWorkedOnFilter
            startDate={lastWorkedOnRange.startDate}
            endDate={lastWorkedOnRange.endDate}
            onChange={(value) => handleFilterChange(setLastWorkedOnRange, value)}
            resetResults={onReset}
          />

                    <HasAppTokenFilter
            selected={selectedHasAppToken}
            onSelect={(value) => handleFilterChange(setSelectedHasAppToken, value)}
            resetResults={onReset}
          />


          <CallCategoryFilter
  selected={selectedCallCategory}
  onSelect={(value) =>
    handleFilterChange(setSelectedCallCategory, value)
  }
  resetResults={onReset}
/>

          {statusFilterError ? (
            <div className="flex flex-col space-y-2">
              <label className="text-sm font-medium text-gray-700">Last Call Status</label>
              <div className="p-2 border border-red-300 rounded bg-red-50 text-red-600 text-sm">
                Failed to load options
              </div>
            </div>
          ) : (
            <LastCallStatusFilter
              selected={selectedLastCallStatus}
              onSelect={(value) => handleFilterChange(setSelectedLastCallStatus, value)}
              resetResults={onReset}
              options={lastCallStatusOptions}
              loading={fetchingStatuses}
              disabled={!selectedCallCategory || selectedCallCategory === "All"}
              disabledReason="Select call category first"
            />
          )}



           {subStatusFilterError ? (
            <div className="flex flex-col space-y-2">
              <label className="text-sm font-medium text-gray-700">Last Call SubStatus</label>
              <div className="p-2 border border-red-300 rounded bg-red-50 text-red-600 text-sm">
                Failed to load options
              </div>
            </div>
          ) : (

<LastCallSubStatusFilter
  selected={selectedLastCallSubStatus}
  onSelect={(value) =>
    handleFilterChange(setSelectedLastCallSubStatus, value)
  }
  resetResults={onReset}
  options={lastCallSubStatusOptions}
  loading={fetchingSubStatuses}
  disabled={!selectedLastCallStatus || selectedLastCallStatus === "All"}
  disabledReason="Select a Last Call Status first"
/>
          )}




          <PaymentverificationFilter
            selected={selectedpaymentverification}
            onSelect={(value) => handleFilterChange(setselectedpaymentverification, value)}
            resetResults={onReset}
          />



          <ShiftCountFilter
            minValue={shiftCountRange.min}
            maxValue={shiftCountRange.max}
            onChange={(v) => {
              if (typeof setShiftCountRange === 'function') {
                setShiftCountRange(v);
              }
              if (typeof onReset === 'function') {
                onReset();
              }
            }}
            resetResults={onReset}
          />

          {/* ─── NEW FILTERS ─────────────────────────────────────────────────── */}
          <PoliceVerificationFilter
            selected={selectedPoliceVerification}
            onSelect={(value) => handleFilterChange(setSelectedPoliceVerification, value)}
            resetResults={onReset}
          />

          <MarksheetFilter  
            selected={selectedMarksheet}  
            onSelect={(value) => handleFilterChange(setSelectedMarksheet, value)}
            resetResults={onReset}
          />

          <EShramFilter
            selected={selectedEshram}
            onSelect={(value) => handleFilterChange(setSelectedEshram, value)}
            resetResults={onReset}
          />
        </>
      )}
    </div>
  );
}